import { useState, useMemo, type ChangeEvent } from 'react'
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { Button, Checkbox, FilterBar } from '@/components/ui'
import {
  useCalendarDepartments,
  useCalendarPlanningTypes,
  useCalendarTanssEmployees,
} from './useCalendarFilterData.ts'
import type { TanssDepartment, PlanningAdditionalType, TanssEmployee } from './useCalendarFilterData.ts'

const STANDARD_TYPES = [
  { type: 'SUPPORT', label: 'Support / Leistung' },
  { type: 'APPOINTMENT_FIX', label: 'Termin (Fix)' },
  { type: 'APPOINTMENT_PROPOSAL', label: 'Terminvorschlag' },
  { type: 'VACATION', label: 'Urlaub' },
  { type: 'ILLNESS', label: 'Krank' },
  { type: 'ABSENCE', label: 'Abwesenheit' },
  { type: 'STAND_BY', label: 'Bereitschaft' },
  { type: 'APPOINTMENT_PRIVATE', label: 'Privat' },
  { type: 'OVERTIME', label: 'Überstunden' },
  { type: 'AUSSENEINSATZ', label: 'Außeneinsatz' },
]

export interface CalendarFilterState {
  departmentIds: number[]
  employeeIds: number[]
  planningTypes: string[]
  planningAdditionalIds: number[]
}

export function emptyCalendarFilter(): CalendarFilterState {
  return { departmentIds: [], employeeIds: [], planningTypes: [], planningAdditionalIds: [] }
}

type FilterSection = 'departments' | 'employees' | 'types'

interface CalendarFilterPanelProps {
  filter: CalendarFilterState
  onChange: (filter: CalendarFilterState) => void
}

function setsEqual<T extends string | number>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()
  return sa.every((v, i) => v === sb[i])
}

// ─── Section toggle button ──────────────────────────────────────────────────

function SectionButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  const Icon = active ? ChevronUp : ChevronDown
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs leading-tight transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
        active
          ? 'bg-brand-50 border-brand-400 text-brand-700 ring-1 ring-brand-300'
          : count > 0
            ? 'bg-brand-400 border-brand-400 text-white hover:bg-brand-500'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
      ].join(' ')}
    >
      <span>{label}</span>
      {count > 0 && (
        <span
          className={[
            'rounded-full px-1.5 text-[10px] font-semibold',
            active ? 'bg-brand-200 text-brand-800' : 'bg-brand-700/40 text-white',
          ].join(' ')}
        >
          {count}
        </span>
      )}
      <Icon className="w-3 h-3 opacity-60" />
    </button>
  )
}

// ─── Generic accordion panel shell ─────────────────────────────────────────

function SectionPanel({
  title,
  search,
  onSearch,
  selectedCount,
  isDirty,
  onSelectAll,
  onSelectNone,
  onReset,
  onApply,
  onClose,
  children,
}: {
  title: string
  search: string
  onSearch: (v: string) => void
  selectedCount: number
  isDirty: boolean
  onSelectAll: () => void
  onSelectNone: () => void
  onReset: () => void
  onApply: () => void
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <span className="font-semibold text-sm text-gray-800">{title}</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition"
          aria-label="Schließen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Suchen…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-7 pr-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      <div className="flex items-center gap-4 px-3 py-1.5 border-b border-gray-100 text-xs">
        <button type="button" onClick={onSelectAll} className="text-brand-600 hover:underline">
          Alle auswählen
        </button>
        <button type="button" onClick={onSelectNone} className="text-brand-600 hover:underline">
          Keine auswählen
        </button>
        <span className="ml-auto text-gray-500">{selectedCount} ausgewählt</span>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 260 }}>
        {children}
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 rounded-b-lg bg-gray-50">
        <Button variant="ghost" size="xs" onClick={onReset}>
          Zurücksetzen
        </Button>
        <Button variant="primary" size="xs" onClick={onApply} disabled={!isDirty}>
          Übernehmen
        </Button>
      </div>
    </div>
  )
}

// ─── Departments panel ──────────────────────────────────────────────────────

function DepartmentsPanel({
  departments,
  draft,
  setDraft,
  appliedFilter,
  search,
  onSearch,
  onApply,
  onClose,
}: {
  departments: TanssDepartment[]
  draft: CalendarFilterState
  setDraft: React.Dispatch<React.SetStateAction<CalendarFilterState>>
  appliedFilter: CalendarFilterState
  search: string
  onSearch: (v: string) => void
  onApply: () => void
  onClose: () => void
}) {
  const filtered = useMemo(
    () => departments.filter((d) => d.name.toLowerCase().includes(search.toLowerCase())),
    [departments, search],
  )

  const isDirty = !setsEqual(draft.departmentIds, appliedFilter.departmentIds)

  function toggle(id: number) {
    setDraft((prev) => ({
      ...prev,
      departmentIds: prev.departmentIds.includes(id)
        ? prev.departmentIds.filter((d) => d !== id)
        : [...prev.departmentIds, id],
    }))
  }

  return (
    <SectionPanel
      title="Abteilungen"
      search={search}
      onSearch={onSearch}
      selectedCount={draft.departmentIds.length}
      isDirty={isDirty}
      onSelectAll={() => setDraft((prev) => ({ ...prev, departmentIds: departments.map((d) => d.id) }))}
      onSelectNone={() => setDraft((prev) => ({ ...prev, departmentIds: [] }))}
      onReset={() => setDraft((prev) => ({ ...prev, departmentIds: [] }))}
      onApply={onApply}
      onClose={onClose}
    >
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-xs text-gray-400">Keine Einträge gefunden</div>
      ) : (
        filtered.map((dept) => (
          <label
            key={dept.id}
            className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 cursor-pointer"
          >
            <Checkbox
              checked={draft.departmentIds.includes(dept.id)}
              onChange={() => toggle(dept.id)}
            />
            <span className="text-xs text-gray-800 flex-1 truncate">{dept.name}</span>
            {dept.employeeIds.length > 0 && (
              <span className="text-[10px] text-gray-400 shrink-0">{dept.employeeIds.length}</span>
            )}
          </label>
        ))
      )}
    </SectionPanel>
  )
}

// ─── Employees panel ────────────────────────────────────────────────────────

function EmployeesPanel({
  employees,
  draft,
  setDraft,
  appliedFilter,
  search,
  onSearch,
  onApply,
  onClose,
}: {
  employees: TanssEmployee[]
  draft: CalendarFilterState
  setDraft: React.Dispatch<React.SetStateAction<CalendarFilterState>>
  appliedFilter: CalendarFilterState
  search: string
  onSearch: (v: string) => void
  onApply: () => void
  onClose: () => void
}) {
  const filtered = useMemo(
    () => employees.filter((e) => e.name.toLowerCase().includes(search.toLowerCase())),
    [employees, search],
  )

  const isDirty = !setsEqual(draft.employeeIds, appliedFilter.employeeIds)

  function toggle(id: number) {
    setDraft((prev) => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(id)
        ? prev.employeeIds.filter((e) => e !== id)
        : [...prev.employeeIds, id],
    }))
  }

  return (
    <SectionPanel
      title="Mitarbeiter"
      search={search}
      onSearch={onSearch}
      selectedCount={draft.employeeIds.length}
      isDirty={isDirty}
      onSelectAll={() => setDraft((prev) => ({ ...prev, employeeIds: employees.map((e) => e.id) }))}
      onSelectNone={() => setDraft((prev) => ({ ...prev, employeeIds: [] }))}
      onReset={() => setDraft((prev) => ({ ...prev, employeeIds: [] }))}
      onApply={onApply}
      onClose={onClose}
    >
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-xs text-gray-400">Keine Einträge gefunden</div>
      ) : (
        filtered.map((emp) => (
          <label
            key={emp.id}
            className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 cursor-pointer"
          >
            <Checkbox
              checked={draft.employeeIds.includes(emp.id)}
              onChange={() => toggle(emp.id)}
            />
            <span className="text-xs text-gray-800 truncate">{emp.name}</span>
          </label>
        ))
      )}
    </SectionPanel>
  )
}

// ─── Types panel ────────────────────────────────────────────────────────────

function TypesPanel({
  planningAdditionalTypes,
  draft,
  setDraft,
  appliedFilter,
  search,
  onSearch,
  onApply,
  onClose,
}: {
  planningAdditionalTypes: PlanningAdditionalType[]
  draft: CalendarFilterState
  setDraft: React.Dispatch<React.SetStateAction<CalendarFilterState>>
  appliedFilter: CalendarFilterState
  search: string
  onSearch: (v: string) => void
  onApply: () => void
  onClose: () => void
}) {
  const filteredStandard = useMemo(
    () => STANDARD_TYPES.filter((t) => t.label.toLowerCase().includes(search.toLowerCase())),
    [search],
  )
  const filteredAdditional = useMemo(
    () => planningAdditionalTypes.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [planningAdditionalTypes, search],
  )

  const isDirty =
    !setsEqual(draft.planningTypes, appliedFilter.planningTypes) ||
    !setsEqual(draft.planningAdditionalIds, appliedFilter.planningAdditionalIds)

  const selectedCount = draft.planningTypes.length + draft.planningAdditionalIds.length

  function toggleType(type: string) {
    setDraft((prev) => ({
      ...prev,
      planningTypes: prev.planningTypes.includes(type)
        ? prev.planningTypes.filter((t) => t !== type)
        : [...prev.planningTypes, type],
    }))
  }

  function toggleAdditional(id: number) {
    setDraft((prev) => ({
      ...prev,
      planningAdditionalIds: prev.planningAdditionalIds.includes(id)
        ? prev.planningAdditionalIds.filter((i) => i !== id)
        : [...prev.planningAdditionalIds, id],
    }))
  }

  return (
    <SectionPanel
      title="Typen"
      search={search}
      onSearch={onSearch}
      selectedCount={selectedCount}
      isDirty={isDirty}
      onSelectAll={() =>
        setDraft((prev) => ({
          ...prev,
          planningTypes: STANDARD_TYPES.map((t) => t.type),
          planningAdditionalIds: planningAdditionalTypes.map((t) => t.id),
        }))
      }
      onSelectNone={() =>
        setDraft((prev) => ({ ...prev, planningTypes: [], planningAdditionalIds: [] }))
      }
      onReset={() =>
        setDraft((prev) => ({ ...prev, planningTypes: [], planningAdditionalIds: [] }))
      }
      onApply={onApply}
      onClose={onClose}
    >
      {filteredStandard.length > 0 && (
        <>
          <div className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
            Standard
          </div>
          {filteredStandard.map((t) => (
            <label
              key={t.type}
              className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 cursor-pointer"
            >
              <Checkbox
                checked={draft.planningTypes.includes(t.type)}
                onChange={() => toggleType(t.type)}
              />
              <span className="text-xs text-gray-800">{t.label}</span>
            </label>
          ))}
        </>
      )}
      {filteredAdditional.length > 0 && (
        <>
          <div className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100 border-t border-t-gray-100">
            Sondertypen
          </div>
          {filteredAdditional.map((t) => (
            <label
              key={t.id}
              className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 cursor-pointer"
            >
              <Checkbox
                checked={draft.planningAdditionalIds.includes(t.id)}
                onChange={() => toggleAdditional(t.id)}
              />
              <span className="text-xs text-gray-800 truncate">{t.name}</span>
            </label>
          ))}
        </>
      )}
      {filteredStandard.length === 0 && filteredAdditional.length === 0 && (
        <div className="px-4 py-3 text-xs text-gray-400">Keine Einträge gefunden</div>
      )}
    </SectionPanel>
  )
}

// ─── Main export ────────────────────────────────────────────────────────────

export function CalendarFilterPanel({ filter, onChange }: CalendarFilterPanelProps) {
  const [openSection, setOpenSection] = useState<FilterSection | null>(null)
  const [draft, setDraft] = useState<CalendarFilterState>(emptyCalendarFilter)
  const [search, setSearch] = useState('')

  const { data: departments = [] } = useCalendarDepartments()
  const { data: planningAdditionalTypes = [] } = useCalendarPlanningTypes()
  const { data: employees = [] } = useCalendarTanssEmployees()

  function openOrClose(section: FilterSection) {
    if (openSection === section) {
      setOpenSection(null)
      setSearch('')
      return
    }
    // Initialize draft from the currently applied filter, discard any previous draft
    setDraft({ ...filter })
    setOpenSection(section)
    setSearch('')
  }

  function applyDraft() {
    onChange(draft)
    // Panel stays open per spec so user can continue adjusting
  }

  function close() {
    setOpenSection(null)
    setSearch('')
  }

  const hasActiveFilters =
    filter.departmentIds.length > 0 ||
    filter.employeeIds.length > 0 ||
    filter.planningTypes.length > 0 ||
    filter.planningAdditionalIds.length > 0

  const activeTypeCount = filter.planningTypes.length + filter.planningAdditionalIds.length

  return (
    <div>
      <FilterBar>
        <SectionButton
          label="Abteilungen"
          count={filter.departmentIds.length}
          active={openSection === 'departments'}
          onClick={() => openOrClose('departments')}
        />
        <SectionButton
          label="Mitarbeiter"
          count={filter.employeeIds.length}
          active={openSection === 'employees'}
          onClick={() => openOrClose('employees')}
        />
        <SectionButton
          label="Typen"
          count={activeTypeCount}
          active={openSection === 'types'}
          onClick={() => openOrClose('types')}
        />
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              onChange(emptyCalendarFilter())
              setOpenSection(null)
            }}
            className="ml-auto text-gray-500"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Zurücksetzen
          </Button>
        )}
      </FilterBar>

      {openSection === 'departments' && (
        <DepartmentsPanel
          departments={departments}
          draft={draft}
          setDraft={setDraft}
          appliedFilter={filter}
          search={search}
          onSearch={setSearch}
          onApply={applyDraft}
          onClose={close}
        />
      )}
      {openSection === 'employees' && (
        <EmployeesPanel
          employees={employees}
          draft={draft}
          setDraft={setDraft}
          appliedFilter={filter}
          search={search}
          onSearch={setSearch}
          onApply={applyDraft}
          onClose={close}
        />
      )}
      {openSection === 'types' && (
        <TypesPanel
          planningAdditionalTypes={planningAdditionalTypes}
          draft={draft}
          setDraft={setDraft}
          appliedFilter={filter}
          search={search}
          onSearch={setSearch}
          onApply={applyDraft}
          onClose={close}
        />
      )}
    </div>
  )
}
